import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography, Grid, ListItemText, ListItem, List, CircularProgress, Alert, TextField, Icon } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { StyledBottomButton, StyledHeadingGrid, StyledGridContainer, StyledNotesButton, StyledPaper } from './styles';

//Components
import RouterBreadcrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";

//custom hook
import { useFetch } from "../../hooks/useFetch";
import { useUpdate } from '../../hooks/useUpdate';

//constants
import { API_URLS } from "../../Constants";

//utils
import { getFormattedPrevYrRank } from '../../utils/getFormattedPrevYrRank';
import { getImpactAreaForRoadSegment } from '../../utils/getImpactAreaForRoadSegment';

//context
import { useConnectedProjectsData } from '../../context/ConnectedProjectsDataContext';

//Breadcrumb path items to edit roadway.
const roadwayPathItems = [
    "Dashboard",
    "Roadways",
    "Edit Roadway"
]

//Invoked for editing a roadway.
export default function EditRoadway() {
    const navigate = useNavigate();
    const location = useLocation();
    const { segmentId, navFromView } = location.state || {};
    const getFetcher = useFetch("get", `${API_URLS["API_GET_ROADWAY_BY_ID"]}?id=${segmentId}`);
    const postFetcher = useUpdate('put', API_URLS["API_UPDATE_ROADWAY"]);

    //React hooks
    const { connectedProjectsData, updateConnectedProjectsData } = useConnectedProjectsData();
    const [roadway, setRoadway] = useState([]);
    const [notes, setNotes] = useState("")
    const [updatedNotes, setUpdatedNotes] = useState("");
    const [edit, setEdit] = useState(false);
    const [previousRanks, setPreviousRanks] = useState([]);


    const [errors, setErrors] = useState([]);

    useEffect(() => {

        if (postFetcher?.serverError && postFetcher?.serverError?.message?.error) {
            //  console.log(postFetcher.serverError.message.error)


            setErrors(postFetcher.serverError.message.error);
        }

    }, [postFetcher.serverError]);

    //Fetch data from API when the component renders.
    useEffect(() => {

        //If no segment id is found, navigate to view projects page.
        if (!segmentId) {
            navigate('/view-roadways');
            return;
        }
        getFetcher.setRequest({});
    }, []);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = getFetcher.serverResponse;
        if (response?.success && response?.data?.length > 0) {
            const formattedPrevYrRanks = getFormattedPrevYrRank(response.data[0].previousRanks);
            const latestTenRanks = formattedPrevYrRanks.sort((a, b) => a.year - b.year).slice(-10);
            setPreviousRanks(latestTenRanks);
            setRoadway(response.data);
            setNotes(response.data[0].notes);
            setUpdatedNotes(response.data[0].notes);
            if (navFromView) {
                const connectedProjects = response.data[0].connectedProjects.map(proj => {
                    return {
                        ...proj,
                        impactArea: proj.impactArea ? getImpactAreaForRoadSegment(proj.impactArea, segmentId) : "None"
                    }
                })
                updateConnectedProjectsData(connectedProjects);
            }
        }
        else if (response?.error)
            console.error('Error in GET request: ', response.error);
    }, [getFetcher.serverResponse]);

    //Navigate to view roadways is response received successfully
    useEffect(() => {
        const response = postFetcher.serverResponse;
        if (response?.success)
            navigate('/view-roadways');
        else if (!response?.success && response?.error)
            console.error('Error in POST request: ', response.error);
    }, [postFetcher.serverResponse]);

    //Imvoked when notes text area changes.
    const handleUpdateNotes = (e) => {
        setUpdatedNotes(e.target.value);
    };

    //Invoked when "Save" button is clicked.
    const handleSaveBtn = () => {
        const updatedRoadway = {
            segmentId: roadway[0].segmentId,
            notes: updatedNotes,
            connectedProjects: connectedProjectsData
        }
        postFetcher.executeUpdate(updatedRoadway);
    }

    //Invoked when "Cancel" button is clicked.
    const handleCancelBtn = () => {

        //Redirect to View Roadways Page.
        navigate('/view-roadways');
    }

    return (
        <Grid container spacing={2}>

            {/* Breadcrumbs for the page */}
            <Grid item xs={12} md={12}>
                <RouterBreadcrumbs pathItems={roadwayPathItems} />
            </Grid>

            {/* Page Title for the page */}
            <Grid item xs={12} md={12}>
                <Title title="Edit Roadway" />
            </Grid>

            {getFetcher.isLoading ? (
                <Grid container justifyContent="center" alignItems="center" style={{ height: '80vh' }}>
                    <CircularProgress />
                </Grid>
            ) : roadway.length > 0 && (
                <>
                    {/* Roadway Information Heading */}
                    <StyledHeadingGrid item xs={12} sx={{ marginTop: "0px" }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Roadway Information
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Roadway Information */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>TTI Segment ID</b>}
                                                secondary={roadway[0].segmentId || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Rank</b>}
                                                secondary={roadway[0].rank || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Number of Projects</b>}
                                                secondary={roadway[0].numOfProjects || "0"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Annual Hours of Delay Per Mile</b>}
                                                secondary={roadway[0].annualDelayMile || "None"}
                                            />
                                        </ListItem>
                                        {roadway[0].annualCongestionCost && (
                                            <ListItem role="presentation">
                                                <ListItemText
                                                    primary={<b>Annual Congestion Cost in $</b>}
                                                    secondary={roadway[0].annualCongestionCost || "None"}
                                                />
                                            </ListItem>
                                        )}
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Year Plan Active</b>}
                                                secondary={roadway[0].yearPlanActive || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Quarter Plan Active</b>}
                                                secondary={roadway[0].quarterPlanActive || "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                            </Grid>
                        </StyledPaper>
                    </Grid>

                    {/* Roadway Specifics Heading */}
                    <StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Roadway Specifics
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Roadway Specifics */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Roadway Title</b>}
                                                secondary={roadway[0].roadwayTitle || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Roadway Name</b>}
                                                secondary={roadway[0].roadwayName || "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>From</b>}
                                                secondary={roadway[0].from || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>To</b>}
                                                secondary={roadway[0].to || "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={4} md={4}>
                                    <List role="presentation">
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>County</b>}
                                                secondary={roadway[0].county || "None"}
                                            />
                                        </ListItem>
                                        <ListItem role="presentation">
                                            <ListItemText
                                                primary={<b>Region(s)</b>}
                                                secondary={roadway[0].region && ('name' in roadway[0].region) ? roadway[0].region['name'] : "None"}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                            </Grid>
                        </StyledPaper>
                    </Grid>

                    {/* Previous Ranks Heading */}
                    <StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Previous Ranks
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Previous Ranks */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            {previousRanks.length === 0 && (
                                <StyledGridContainer container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No data available.</Typography>
                                    </Grid>
                                </StyledGridContainer>
                            )}
                            {previousRanks?.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }} aria-label="Previous Ranks">
                                    <thead>
                                        <tr style={{ textAlign: 'left' }}>
                                            <th style={{ padding: '8px' }}>Year</th>
                                            {previousRanks.map((row, index) => (
                                                <th key={`year-head-${index}`} style={{ padding: '8px', fontWeight: 'bold', fontStyle: 'italic' }}>
                                                    {row.year}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Ranks</td>
                                            {previousRanks.map((row, index) => (
                                                <td key={`rank-data-${index}`} style={{ padding: '8px' }}>
                                                    {row.rank}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </StyledPaper>
                    </Grid>

                    {/* Connected Projects Heading */}
                    < StyledHeadingGrid item xs={12}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Connected Projects
                            <IconButton aria-label="Edit connected projects" onClick={() => { navigate(`/connected-projects`, { state: { selectedRoadway: roadway } }) }}>
                                <EditIcon />
                            </IconButton>
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Connected Projects */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            {connectedProjectsData && connectedProjectsData.length > 0 ? (
                                <>
                                    <StyledGridContainer container spacing={2} sx={{ marginBottom: "25px" }}>
                                        <Grid item xs={4} sx={{ marginTop: "20px", fontWeight: 'bold' }}>
                                            Project Title
                                        </Grid>
                                        <Grid item xs={4} sx={{ marginTop: "20px", fontWeight: 'bold' }}>
                                            Project Type
                                        </Grid>
                                        <Grid item xs={4} sx={{ marginTop: "20px", fontWeight: 'bold' }}>
                                            Impact Area
                                        </Grid>
                                    </StyledGridContainer>
                                    {connectedProjectsData.map((row, index) =>
                                        <StyledGridContainer container spacing={2} key={index} sx={{ paddingBottom: "25px" }}>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">
                                                    {row.projectTitle || "None"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">
                                                    {row.projectType || "None"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="body2">
                                                    {row.impactArea}
                                                </Typography>
                                            </Grid>
                                        </StyledGridContainer>
                                    )}
                                </>
                            ) : (
                                <StyledGridContainer container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No connected projects available. Click edit icon to add connections.</Typography>
                                    </Grid>
                                </StyledGridContainer>
                            )}
                        </StyledPaper>
                    </Grid>


                    {/* Notes Heading */}
                    <StyledHeadingGrid item xs={12}>
                        <Typography id="notes-label" variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                            Notes
                            <Typography style={{ fontSize: 12, fontStyle: 'italic', display: 'inline', marginLeft: '10px' }}>
                                These notes won't be published
                            </Typography>
                        </Typography>
                    </StyledHeadingGrid>

                    {/* Display Notes */}
                    <Grid item xs={12} md={12}>
                        <StyledPaper elevation={3}>
                            <Grid item xs={12}>
                                <TextField
                                    id="notes-textfield"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    variant='outlined'
                                    placeholder="Enter notes here..."
                                    value={updatedNotes}
                                    onChange={handleUpdateNotes}
                                    inputProps={{
                                        id: 'notes-input',
                                        'aria-labelledby': 'notes-label'
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input::placeholder": {
                                            color: "black",
                                            opacity: 1,
                                        },
                                    }}
                                />
                            </Grid>
                        </StyledPaper>
                    </Grid>


                    <Grid item >
                        {errors?.length > 0 && (
                            <Alert severity="error" style={{ color: "#14375A" }}>
                                <strong>{errors}</strong>
                            </Alert>
                        )}
                    </Grid>

                    {/* Display Buttons */}
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <>
                            <StyledBottomButton aria-label="Cancel roadway changes" variant="contained" color="primary" onClick={handleCancelBtn}>
                                Cancel
                            </StyledBottomButton>
                            <StyledBottomButton aria-label="Save all roadway changes" variant="contained" color="primary" onClick={handleSaveBtn}>
                                Save
                            </StyledBottomButton>
                        </>
                    </Grid>
                </>
            )}
        </Grid>
    );
}